import isEmpty from 'lodash/isEmpty';
import { InfoBanner } from 'ui/banner';
import { PAGE_SIZES, PaginationFooter } from 'ui/paginationFooter';
import { FullWidth } from 'ui/utility/fullWidth';

import { UsageTable } from './UsageTable';
import { EmptyStateNotFound, NoTemplates, NoResults } from './EmptyState';
import { useSnippetById } from '../hooks/useSnippetById';

import { BannerContainer } from './Styled';
import { SnippetReferenceFilter } from './SnippetReferenceFilter';
import { SnippetReferenceHeader } from './SnippetReferenceHeader';
import { useReferencePaginationContext } from '../context/ReferencesPaginationContext';
import { useSnippetReferencesFilter } from '../context/SnippetReferencesFilterContext';
import { useSnippetReferences } from '../hooks/useSnippetReferences';
import { useTranslations } from '../hooks/useTranslations';

export const SnippetReferences = ({ match }: { match: any }) => {
  const { t } = useTranslations();
  const snippetId = match.params.id ? parseInt(match.params.id, 10) : 0;
  const { hasError = false, results: snippet } = useSnippetById({ id: snippetId });

  const { activeTotal, allReferences, referencesByPage, isLoading, isUpdating } =
    useSnippetReferences({
      snippetName: snippet.name,
    });

  const { limit, currentPage, handlePaginationChange } = useReferencePaginationContext();

  const { selectedFilters, resetFilter, handleFilterChange } = useSnippetReferencesFilter();

  if (hasError || (!referencesByPage && snippet.id === 0)) {
    return <EmptyStateNotFound />;
  }

  const hasReferences = !isEmpty(allReferences);
  const hasReferencesByPage = !isEmpty(referencesByPage);

  return (
    <>
      <SnippetReferenceHeader
        rows={activeTotal}
        id={snippet.id}
        name={snippet.name}
        hasResults={hasReferencesByPage}
      />

      <BannerContainer>
        <InfoBanner>{t('banner.snippetReferences')}</InfoBanner>
      </BannerContainer>

      {(hasReferences || hasReferencesByPage) && (
        <SnippetReferenceFilter
          selectedFilters={selectedFilters}
          onFilterChange={handleFilterChange}
          clearFilter={resetFilter}
        />
      )}

      {(isLoading || hasReferences) && (
        <FullWidth>
          <UsageTable results={referencesByPage} isLoading={isLoading} isUpdating={isUpdating} />
        </FullWidth>
      )}

      {!isLoading && !hasReferencesByPage && (hasReferences ? <NoResults /> : <NoTemplates />)}

      {hasReferencesByPage && (
        <FullWidth>
          <PaginationFooter
            currentPage={currentPage}
            itemsPerPage={limit}
            numItems={activeTotal.length}
            pageSizes={PAGE_SIZES}
            onUpdate={handlePaginationChange}
          />
        </FullWidth>
      )}
    </>
  );
};
